/**
 * UnresolvedSummary — counter для unresolved rows (§11.5).
 *
 * Block M1a-FE (TZ 2026-07-29). Показывает только count + honest summary.
 * Final results table для unresolved — M1b (§14.4).
 *
 * Source of truth: per-row entry — `r.mapped_data.rows[r.id]?.status`. BE
 * writer `services/matching/writer.merge_write_mapped_data` mirrors the
 * FULL `mapped_data.rows` dict onto every DatasetRow's JSONB column (same
 * dataset state, per-row delivery). So we MUST read the entry keyed by
 * the DatasetRow's own UUID (`r.id`), NOT iterate the dict per row — that
 * would yield N×N counts.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';

import { usePipelineMatchingRows } from '@/hooks/useMatching';

export interface UnresolvedSummaryProps {
  pipelineId: string | null;
}

export function UnresolvedSummary({ pipelineId }: UnresolvedSummaryProps) {
  const { t } = useTranslation();
  const { data: rowsResp } = usePipelineMatchingRows(pipelineId);

  const count = useMemo(() => {
    const rows = rowsResp?.rows ?? [];
    let n = 0;
    for (const r of rows) {
      // Per-row: read the entry keyed by THIS row's own UUID. Writer uses
      // `str(row_id)` as the dict key, so `r.id` (UUID string) matches it.
      const md = r.mapped_data as
        | {
            rows?: Record<string, { status?: string }>;
          }
        | null
        | undefined;
      const entry = md?.rows?.[r.id];
      if (entry?.status === 'unresolved') n++;
    }
    return n;
  }, [rowsResp]);

  if (!pipelineId || count === 0) return null;

  return (
    <section className="card space-y-1">
      <h2 className="text-sm font-semibold uppercase tracking-wider text-surface-400 flex items-center gap-2">
        <AlertCircle className="w-4 h-4 text-warning" />
        {t('process.matching.results.unresolved.title', {
          defaultValue: 'Unresolved queue',
        })}
      </h2>
      <p className="text-xs text-surface-300">
        {t('process.matching.results.unresolved.summary', {
          defaultValue: '{n} unresolved rows',
          n: count,
        })}
      </p>
    </section>
  );
}
